import { UseQueryResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { WorkflowRuleDto, ResponseDtoWorkflowRuleDto, ResponseDtoListWorkflowRuleDto, ResponseDtoString } from 'src/generated/services/TFinancialApi';
import { tFinancialService } from 'src/infra/services/tFinancialService';

/**
 * Hook to fetch all workflow rules
 * @returns Query result with all workflow rules
 */
export const useWorkflowRuleList = (): UseQueryResult<ResponseDtoListWorkflowRuleDto> =>
  useQuery(['workflowRules'], async () => {
    const response = await tFinancialService.api.getAllWorkflowRules();
    return response.data;
  });

/**
 * Hook to fetch a single workflow rule by ID
 * @param id Workflow rule ID
 * @returns Query result with workflow rule data
 */
export const useWorkflowRuleById = (id: number): UseQueryResult<ResponseDtoWorkflowRuleDto> =>
  useQuery(
    ['workflowRule', id],
    async () => {
      const response = await tFinancialService.api.getWorkflowRuleById(id);
      return response.data;
    },
    {
      enabled: !!id, // Only fetch if id is provided
    }
  );

/**
 * Hook to create or update a workflow rule
 * @returns Mutation function for creating/updating workflow rules
 */
export const useUpsertWorkflowRule = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (workflowRule: WorkflowRuleDto): Promise<ResponseDtoWorkflowRuleDto> => {
      const response = await tFinancialService.api.createOrUpdateWorkflowRule(workflowRule);
      return response.data;
    },
    {
      onSuccess: () => {
        // Invalidate relevant queries to refetch data
        queryClient.invalidateQueries(['workflowRules']);
        queryClient.invalidateQueries(['workflows']); // Invalidate workflows list
        queryClient.invalidateQueries(['workflow']); // Invalidate workflow detail (by ID)
      },
    }
  );
};

/**
 * Hook to delete a workflow rule
 * @returns Mutation function for deleting workflow rules
 */
export const useDeleteWorkflowRule = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (id: number): Promise<ResponseDtoString> => {
      const response = await tFinancialService.api.deleteWorkflowRule(id);
      return response.data;
    },
    {
      onSuccess: () => {
        // Invalidate relevant queries to refetch data
        queryClient.invalidateQueries(['workflowRules']);
        queryClient.invalidateQueries(['workflows']); // Invalidate workflows list
        queryClient.invalidateQueries(['workflow']); // Invalidate workflow detail (by ID)
      },
    }
  );
};
